import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createEquityGrant,
  updateEquityGrant,
} from "../../lib/api-modules/equity-grants.js";
import {
  EQUITY_GRANT_TYPE_LABELS,
  type BlackoutWindow,
  type CreateEquityGrantInput,
  type EquityGrant,
  type EquityGrantType,
  type UpdateEquityGrantInput,
  type VestEvent,
} from "../../lib/api-modules/types.js";
import { centsToDollars, dollarsToCents } from "../../lib/util/money.js";
import { generateVestEvents } from "../../lib/util/equity.js";
import { ErrorMessage } from "../auth/error-message.js";

interface EquityGrantFormProps {
  readonly mode: "create" | "edit";
  readonly grant?: EquityGrant;
  readonly onClose: () => void;
}

const GRANT_TYPE_OPTIONS = (
  Object.keys(EQUITY_GRANT_TYPE_LABELS) as EquityGrantType[]
).map((v) => ({ value: v, label: EQUITY_GRANT_TYPE_LABELS[v] }));

export function EquityGrantForm({
  mode,
  grant,
  onClose,
}: EquityGrantFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const today = new Date().toISOString().slice(0, 10);

  const [grantType, setGrantType] = useState<EquityGrantType>(
    grant?.grant_type ?? "RSU",
  );
  const [ticker, setTicker] = useState<string>(grant?.ticker ?? "");
  const [grantDate, setGrantDate] = useState<string>(
    grant?.grant_date ?? today,
  );
  const [totalShares, setTotalShares] = useState<string>(
    grant ? String(grant.total_shares) : "",
  );
  const [strikeDollars, setStrikeDollars] = useState<string>(
    grant?.strike_price_cents !== undefined
      ? centsToDollars(grant.strike_price_cents)
      : "",
  );
  const [fmvDollars, setFmvDollars] = useState<string>(
    grant?.most_recent_fmv_cents !== undefined
      ? centsToDollars(grant.most_recent_fmv_cents)
      : "",
  );
  const [fmvAsOf, setFmvAsOf] = useState<string>(grant?.fmv_as_of_date ?? "");
  const [expirationDate, setExpirationDate] = useState<string>(
    grant?.expiration_date ?? "",
  );
  const [notes, setNotes] = useState<string>(grant?.notes ?? "");

  // Schedule generator (create-only; edits operate on vest_events directly)
  const [cliffMonths, setCliffMonths] = useState<string>("12");
  const [vestMonths, setVestMonths] = useState<string>("48");
  const [periodMonths, setPeriodMonths] = useState<string>("1");

  const [vestEvents, setVestEvents] = useState<VestEvent[]>(
    grant?.vest_events ?? [],
  );
  const [blackouts, setBlackouts] = useState<BlackoutWindow[]>(
    grant?.blackout_windows ?? [],
  );

  const requiresStrike = grantType === "ISO" || grantType === "NSO";
  const forbidsStrike = grantType === "RSU" || grantType === "RSA";

  const previewError = useMemo(() => {
    if (!totalShares || !grantDate) return null;
    try {
      generateVestEvents({
        grantDate,
        totalShares: Number(totalShares),
        cliffMonths: Number(cliffMonths),
        vestMonths: Number(vestMonths),
        periodMonths: Number(periodMonths),
      });
      return null;
    } catch (e) {
      return (e as Error).message;
    }
  }, [totalShares, grantDate, cliffMonths, vestMonths, periodMonths]);

  const onGenerate = () => {
    try {
      const generated = generateVestEvents({
        grantDate,
        totalShares: Number(totalShares),
        cliffMonths: Number(cliffMonths),
        vestMonths: Number(vestMonths),
        periodMonths: Number(periodMonths),
      });
      setVestEvents(generated);
    } catch {
      // surfaced via previewError
    }
  };

  const create = useMutation({
    mutationFn: (input: CreateEquityGrantInput) => createEquityGrant(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["equity-grants"] });
      onClose();
    },
  });
  const update = useMutation({
    mutationFn: (input: UpdateEquityGrantInput) =>
      updateEquityGrant(grant!.grant_id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["equity-grants"] });
      onClose();
    },
  });
  const m = isEdit ? update : create;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const base = {
      ticker: ticker.trim().toUpperCase(),
      total_shares: Number(totalShares),
      vest_events: vestEvents,
      blackout_windows: blackouts,
      ...(strikeDollars && !forbidsStrike
        ? { strike_price_cents: dollarsToCents(strikeDollars) }
        : {}),
      ...(expirationDate ? { expiration_date: expirationDate } : {}),
      ...(fmvDollars
        ? { most_recent_fmv_cents: dollarsToCents(fmvDollars) }
        : {}),
      ...(fmvAsOf ? { fmv_as_of_date: fmvAsOf } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    if (isEdit) {
      update.mutate(base);
    } else {
      create.mutate({
        ...base,
        grant_type: grantType,
        grant_date: grantDate,
      });
    }
  };

  const totalVested = vestEvents.reduce((s, v) => s + v.shares, 0);

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-md border border-zinc-300 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-900"
    >
      <h3 className="mb-3 text-sm font-semibold">
        {isEdit ? `Edit grant ${grant?.ticker}` : "Add equity grant"}
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs">
          Type
          <select
            value={grantType}
            onChange={(e) => setGrantType(e.target.value as EquityGrantType)}
            disabled={isEdit}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm disabled:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-950"
          >
            {GRANT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Ticker
          <input
            value={ticker}
            onChange={(e) => setTicker(e.target.value)}
            required
            placeholder="e.g. ACME"
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm uppercase dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Grant date
          <input
            type="date"
            value={grantDate}
            onChange={(e) => setGrantDate(e.target.value)}
            disabled={isEdit}
            required
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm disabled:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Total shares
          <input
            type="number"
            step="any"
            min="0"
            value={totalShares}
            onChange={(e) => setTotalShares(e.target.value)}
            required
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        {!forbidsStrike && (
          <label className="flex flex-col gap-1 text-xs">
            Strike (USD/share)
            {requiresStrike && (
              <span className="text-[10px] text-amber-700">required</span>
            )}
            <input
              type="number"
              step="0.01"
              min="0"
              value={strikeDollars}
              onChange={(e) => setStrikeDollars(e.target.value)}
              required={requiresStrike}
              className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>
        )}
        <label className="flex flex-col gap-1 text-xs">
          Expiration (opt)
          <input
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Most recent FMV (USD/share)
          <input
            type="number"
            step="0.01"
            min="0"
            value={fmvDollars}
            onChange={(e) => setFmvDollars(e.target.value)}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          FMV as-of (opt)
          <input
            type="date"
            value={fmvAsOf}
            onChange={(e) => setFmvAsOf(e.target.value)}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
      </div>

      {/* Schedule generator — create only */}
      {!isEdit && (
        <fieldset className="mt-4 rounded-md border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-950">
          <legend className="px-1 text-xs font-semibold">Vest schedule generator</legend>
          <p className="mb-2 text-xs text-zinc-500">
            Defaults to a 1-year cliff + monthly over 4 years. Fill in
            irregular vests after generating, or paste vest_events JSON
            directly.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <label className="flex flex-col gap-1 text-xs">
              Cliff (months)
              <input
                type="number"
                min="0"
                value={cliffMonths}
                onChange={(e) => setCliffMonths(e.target.value)}
                className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              Total vesting (months)
              <input
                type="number"
                min="1"
                value={vestMonths}
                onChange={(e) => setVestMonths(e.target.value)}
                className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              Period (months)
              <input
                type="number"
                min="1"
                value={periodMonths}
                onChange={(e) => setPeriodMonths(e.target.value)}
                className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              />
            </label>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={onGenerate}
              disabled={previewError !== null}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-100 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-800"
            >
              Generate schedule
            </button>
            {previewError && (
              <span className="text-[11px] text-red-600">{previewError}</span>
            )}
          </div>
        </fieldset>
      )}

      {/* Vest events preview */}
      {vestEvents.length > 0 && (
        <details className="mt-4 rounded-md border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-950" open>
          <summary className="cursor-pointer text-xs font-semibold">
            Vest events ({vestEvents.length}; total shares {totalVested.toLocaleString(undefined, { maximumFractionDigits: 4 })})
          </summary>
          <ul className="mt-2 max-h-48 overflow-auto text-xs">
            {vestEvents.map((v, i) => (
              <li
                key={i}
                className="flex items-center justify-between border-b border-zinc-100 py-1 last:border-b-0 dark:border-zinc-900"
              >
                <span>
                  <time dateTime={v.date}>{v.date}</time>
                  {v.is_cliff && (
                    <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      cliff
                    </span>
                  )}
                </span>
                <span className="font-mono">
                  {v.shares.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Blackout windows */}
      <BlackoutEditor windows={blackouts} onChange={setBlackouts} />

      <label className="mt-3 flex flex-col gap-1 text-xs">
        Notes
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
        />
      </label>
      {m.error && (
        <ErrorMessage>
          {m.error instanceof ApiError ? m.error.message : "Couldn't save"}
        </ErrorMessage>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={m.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:opacity-60"
        >
          {m.isPending ? "Saving…" : isEdit ? "Save" : "Add"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function BlackoutEditor({
  windows,
  onChange,
}: {
  windows: BlackoutWindow[];
  onChange: (next: BlackoutWindow[]) => void;
}) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");

  return (
    <fieldset className="mt-4 rounded-md border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-950">
      <legend className="px-1 text-xs font-semibold">Blackout windows</legend>
      {windows.length === 0 ? (
        <p className="text-xs text-zinc-500">None.</p>
      ) : (
        <ul className="mb-2 text-xs">
          {windows.map((w, i) => (
            <li
              key={i}
              className="flex items-center justify-between border-b border-zinc-100 py-1 last:border-b-0 dark:border-zinc-900"
            >
              <span>
                {w.start} → {w.end} · {w.reason}
              </span>
              <button
                type="button"
                onClick={() =>
                  onChange(windows.filter((_, j) => j !== i))
                }
                className="text-[11px] text-red-600 hover:underline"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <input
          type="date"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
        />
        <input
          type="date"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
        />
        <input
          type="text"
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="flex-1 rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
        />
        <button
          type="button"
          onClick={() => {
            if (!start || !end || !reason || start > end) return;
            onChange([...windows, { start, end, reason }]);
            setStart("");
            setEnd("");
            setReason("");
          }}
          className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
        >
          Add window
        </button>
      </div>
    </fieldset>
  );
}
